import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { unregisterGuest } from '../api/events.guest.api';
import { EVENTS_STRINGS } from '../constants/events-strings';
import { clearGuestRegistration } from '../guest-registration-storage';
import { guestRegistrationKeys } from './events.keys';

/**
 * Cancel a remembered non-member's registration (FR-289). The handlers live on
 * the mutation so they survive the confirm dialog unmounting.
 *
 * Success: a toast, the stored entry for this event is cleared, the cached
 * status answers for the event are dropped, and `onUnregistered` lets the page
 * re-read its state so Register shows again. The cancel route answers alike for
 * every token, so the entry is cleared on any success.
 *
 * Failure is NOT toasted: the dialog is still open, so it shows the reason
 * inline and nothing is cleared, leaving the action retryable (mnr-standards,
 * write-action feedback; never both channels for one event).
 *
 * @param eventId        Target event.
 * @param onUnregistered Called once the entry is cleared, to re-read the remembered state.
 */
export function useGuestUnregister(eventId: string, onUnregistered?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => unregisterGuest(eventId, token),
    onSuccess: () => {
      toast.success(EVENTS_STRINGS.GUEST.UNREGISTER_SUCCESS);
      clearGuestRegistration(eventId);
      queryClient.removeQueries({ queryKey: guestRegistrationKeys.forEvent(eventId) });
      onUnregistered?.();
    },
  });
}
